import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    target?.addEventListener(StandardEvents.productSelect, this.#handleMediaGroupSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Switches the visible media group before the section has been re-rendered.
   *
   * When media grouping is enabled every group is rendered into the gallery and the inactive ones
   * are hidden, so the group can be swapped synchronously on click. The server-rendered gallery
   * still replaces this one when the fetch resolves; this only removes the wait from the swap.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleMediaGroupSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const optionName = this.dataset.mediaGroupOption;
    if (!this.hasAttribute('data-media-grouping-enabled') || !optionName) return;

    const selectedOption = event.selectedOptions?.find((option) => option.name === optionName);
    const group = selectedOption?.value;
    if (!group || group === this.dataset.mediaGroup) return;

    const groupedElements = this.querySelectorAll('[data-media-group]');
    if (!groupedElements.length) return;

    /** @type {Element | null} */
    let firstSlideInGroup = null;
    let visibleSlideCount = 0;

    for (const element of groupedElements) {
      const matches = element.getAttribute('data-media-group') === group;

      element.toggleAttribute('hidden', !matches);
      element.removeAttribute('reveal');

      if (!matches || element.tagName !== 'SLIDESHOW-SLIDE') continue;

      visibleSlideCount++;
      firstSlideInGroup ??= element;
    }

    // Nothing in the new group - leave the gallery as it was and let the section render fix it up.
    if (!visibleSlideCount) {
      for (const element of groupedElements) {
        element.toggleAttribute('hidden', element.getAttribute('data-media-group') !== this.dataset.mediaGroup);
      }
      return;
    }

    this.dataset.mediaGroup = group;
    this.#syncControlsToVisibleCount(visibleSlideCount);

    const slideId = firstSlideInGroup?.getAttribute('slide-id');
    if (slideId) this.slideshow?.select({ id: slideId }, undefined, { animate: false });
  };

  /**
   * Hides the pagination controls that the current group has no media for. The controls are
   * rendered for the largest group, so only trailing ones ever need to be hidden.
   *
   * @param {number} visibleSlideCount - The number of slides in the selected group.
   */
  #syncControlsToVisibleCount(visibleSlideCount) {
    for (const controls of this.querySelectorAll('slideshow-controls')) {
      controls.querySelectorAll('.slideshow-controls__dots > li').forEach((dot, index) => {
        dot.toggleAttribute('hidden', index >= visibleSlideCount);
      });

      // The total is the bare text node after the slash, so it can be updated without touching
      // the [ref] element the slideshow writes the current index into.
      const total = controls.querySelector('.slideshow-controls__counter')?.lastChild;
      if (total?.nodeType === Node.TEXT_NODE) total.textContent = `${visibleSlideCount}`;
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
